import { BookOpen, Users, Sun, Footprints, Compass, Globe, Lightbulb } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { HowSupportBegins, WhereSupportHappens } from "@/components/SupportJourney";

const PILLARS = [
  { icon: BookOpen, name: "Story", desc: "Narrative therapy and narrative conversations — separating people from problems and making room for preferred stories." },
  { icon: Users, name: "Connection", desc: "Relationships with family, carers, community and peers — because healing happens in relationship, not isolation." },
  { icon: Sun, name: "Culture", desc: "Identity, belonging, family, Country and connection to appropriate cultural knowledge holders." },
  { icon: Footprints, name: "Movement", desc: "Walking, fitness, outdoor activities and side-by-side conversations — sometimes it's easier to yarn while moving." },
  { icon: Compass, name: "Mentoring", desc: "Consistent relationships and practical support that show up in real life, not just in appointments." },
  { icon: Globe, name: "Community", desc: "Creating networks around children rather than creating dependence on services." },
  { icon: Lightbulb, name: "Knowledge", desc: "Recognising children, families and community as knowledge holders — lived experience that can help the next person." },
];

const Approach = () => (
  <div data-testid="approach-page">
    <PageHero
      kicker="Our Approach"
      title={"One relationship.\nSeven ways of showing up."}
      lede="Ngawilyi's Aunty model allows therapeutic, mentoring and practical support to occur together — responding to what is actually happening in a young person's life, not just what a referral category says."
    />

    <section className="px-6 py-20 sm:py-28 bg-cream">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.07} className={i === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
              <div
                data-testid={`pillar-${p.name.toLowerCase()}`}
                className={`group rounded-[36px] p-9 h-full transition-all duration-500 hover:-translate-y-2 ${
                  i === 0 ? "bg-terracotta text-cream" : i === 3 ? "bg-charcoal text-cream" : "bg-creamdark border border-ochre/25 text-charcoal"
                }`}
              >
                <div className="flex items-start justify-between">
                  <p.icon className={`h-8 w-8 ${i === 0 ? "text-cream" : i === 3 ? "text-ochre" : "text-terracotta"}`} strokeWidth={1.5} />
                  <span className="font-display italic text-lg opacity-50">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-8 font-display text-3xl font-light uppercase tracking-wide">{p.name}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${i === 0 || i === 3 ? "text-cream/80" : "text-earth/85"}`}>{p.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.5} className="md:col-span-2">
            <div className="rounded-[36px] bg-blush/50 p-9 h-full flex flex-col justify-center">
              <p className="font-display italic text-3xl sm:text-4xl text-charcoal leading-snug">
                Healing through story. <span className="text-terracotta">Strength through connection.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <HowSupportBegins />
    <WhereSupportHappens />

    <section className="px-6 py-20 sm:py-28 bg-creamdark grain relative overflow-hidden">
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-terracotta/15 organic-blob blur-3xl" />
      <div className="relative max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Kicker>Not an office-only service</Kicker>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal">
            Important conversations often happen <span className="italic text-terracotta">side-by-side.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-base lg:text-lg leading-relaxed text-earth">
            Ngawilyi recognises that not everyone wants to sit opposite a professional in an office and talk.
            A yarn on a walk, a letter written over lunch, a gym session, a cook-up — these are not distractions
            from the work. They are the work. Support happens where life happens.
          </p>
          <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
            And always: <strong className="text-charcoal">the person is the knowledge holder in their own life.</strong>{" "}
            Our practitioners never position themselves as the expert on somebody else's life — we walk alongside,
            helping the knowledge, strengths and hopes already there become more visible.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand
      title="One trusted Aunty, drawing from a range of program models depending on what life brings."
      primary={{ to: "/programs", label: "Explore Programs" }}
      secondary={{ to: "/threads", label: "Finding the Threads" }}
    />
  </div>
);

export default Approach;
